import { axiosInstance } from "../../utils/axios";
import { Rule } from "../../components/interfaces/Rule";
import { Tag } from "../../components/interfaces/Tag";
import type { ConsistencyTagErrorKind } from "../../utils/helpers/RulesRunnerHelper";

export interface ToolCall {
  tool: string;
  params: Record<string, unknown>;
}

export interface ValidationAgentResponse {
  tool_calls?: ToolCall[];
}

export interface ValidationAgentConversationMessage {
  role: "user" | "assistant";
  content: string;
}

export type EventType = "USER_MESSAGE" | "LAUNCH_RESULTS";

export interface LaunchResultTagRef {
  tagId: string;
  label: string;
  value?: string;
  status?: string;
  values?: string[];
  errorKind?: ConsistencyTagErrorKind;
}

export interface LaunchResult {
  ruleId: string;
  ruleType: "CUSTOM" | "CONSISTENCY" | "TABLE_TOTALS";
  status: "VERIFIED" | "FAILED" | "CANNOT_EXECUTE";
  referencedTags?: LaunchResultTagRef[];
  errorReason?: "MISSING_TAG" | "MISSING_OBJECTS" | "NON_NUMERIC_VALUE" | "SCRIPT_ERROR";
}

export interface ExecuteValidationAgentParams {
  documentId: string;
  conversation: ValidationAgentConversationMessage[];
  existingTags?: Tag[];
  existingRules?: Rule[];
  stagedTags?: Tag[];
  stagedRules?: Rule[];
  eventType?: EventType;
  launchResults?: LaunchResult[];
  pendingTagEdits?: { targetId: string; patch: Tag }[];
  pendingRuleEdits?: { targetId: string; patch: Rule }[];
}

function resolveExecuteValidationAgentParams(
  args:
    | [ExecuteValidationAgentParams]
    | [string, ValidationAgentConversationMessage[], Tag[]?, Rule[]?, Tag[]?, Rule[]?, EventType?, LaunchResult[]?]
): ExecuteValidationAgentParams {
  const [firstArg] = args;
  if (typeof firstArg !== "string") {
    return firstArg;
  }

  const [documentId, conversation, existingTags, existingRules, stagedTags, stagedRules, eventType, launchResults] =
    args as [
      string,
      ValidationAgentConversationMessage[],
      Tag[]?,
      Rule[]?,
      Tag[]?,
      Rule[]?,
      EventType?,
      LaunchResult[]?,
    ];
  return {
    documentId,
    conversation,
    existingTags,
    existingRules,
    stagedTags,
    stagedRules,
    eventType,
    launchResults,
  };
}

export async function executeValidationAgent(
  ...args:
    | [ExecuteValidationAgentParams]
    | [string, ValidationAgentConversationMessage[], Tag[]?, Rule[]?, Tag[]?, Rule[]?, EventType?, LaunchResult[]?]
): Promise<ValidationAgentResponse> {
  const {
    documentId,
    conversation,
    existingTags,
    existingRules,
    stagedTags,
    stagedRules,
    eventType,
    launchResults,
    pendingTagEdits,
    pendingRuleEdits,
  } = resolveExecuteValidationAgentParams(args);
  const response = await axiosInstance.post<ValidationAgentResponse>(
    "/agent/validation",
    {
      documentId,
      conversation,
      existingTags,
      existingRules,
      stagedTags,
      stagedRules,
      eventType,
      launchResults,
      pendingTagEdits,
      pendingRuleEdits,
    },
    { params: { notShowLoading: true } }
  );
  return response.data;
}

export interface SendLaunchResultsOptions {
  documentId: string;
  conversation: ValidationAgentConversationMessage[];
  launchResults: LaunchResult[];
  existingTags?: Tag[];
  existingRules?: Rule[];
  stagedTags?: Tag[];
  stagedRules?: Rule[];
  pendingTagEdits?: { targetId: string; patch: Tag }[];
  pendingRuleEdits?: { targetId: string; patch: Rule }[];
}

export async function sendLaunchResults(options: SendLaunchResultsOptions): Promise<ValidationAgentResponse> {
  return executeValidationAgent({
    ...options,
    eventType: "LAUNCH_RESULTS",
  });
}
